<script lang="ts">
    import { onMount } from 'svelte';
    import { push } from 'svelte-spa-router';
    import { api } from '../../framework/js/api.js';
    import { toast } from '../../framework/js/toast.js';
    import { spinner } from '../../framework/js/spinner.js';
    import { StatusBadge } from '../../framework/components/app';
    import type { StatusTone } from '../../framework/components/app';

    interface TeamOwner {
        uuid: string | null;
        name: string;
        email: string | null;
    }

    interface TeamRow {
        uuid: string;
        name: string;
        payment_plan?: { name: string } | null;
        billing_interval?: string | null;
        is_on_trial?: boolean;
        trial_days_remaining?: number | null;
        seat_count?: number;
        active_member_count?: number;
        pending_invitation_count?: number;
        cancellation_date?: string | null;
        renewal_date?: string | null;
        created_at?: string;
        owners?: TeamOwner[];
    }

    const STATUS_OPTIONS = [
        { value: '', label: 'All statuses' },
        { value: 'trial', label: 'On trial' },
        { value: 'active', label: 'Active subscription' },
        { value: 'cancelled', label: 'Cancelled' },
        { value: 'no_plan', label: 'No plan' },
    ];

    let teams: TeamRow[] = [];
    let filteredTeams: TeamRow[] = [];
    let isLoading = false;
    let searchQuery = '';
    let statusFilter = '';
    let sortField: 'name' | 'created_at' | 'active_member_count' = 'created_at';
    let sortDirection: 'asc' | 'desc' = 'desc';

    onMount(async () => {
        await loadTeams();
    });

    async function loadTeams() {
        isLoading = true;
        spinner.show();
        try {
            const params: Record<string, string> = {};
            if (searchQuery.trim()) params.search = searchQuery.trim();
            if (statusFilter) params.status = statusFilter;
            params.sort = sortField;
            params.sort_dir = sortDirection;

            const response: any = await api.getAdminTeams(params);
            if (response.success) {
                teams = response.data?.teams ?? [];
                applyClientSort();
            } else {
                toast.showNotification(false, response.message || 'Failed to load teams');
                teams = [];
                filteredTeams = [];
            }
        } catch (error) {
            toast.showNotification(false, 'Error loading teams');
            console.error(error);
            teams = [];
            filteredTeams = [];
        } finally {
            spinner.hide();
            isLoading = false;
        }
    }

    function applyClientSort() {
        filteredTeams = [...teams];
    }

    function handleSort(field: typeof sortField) {
        if (sortField === field) {
            sortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
            sortField = field;
            sortDirection = field === 'name' ? 'asc' : 'desc';
        }
        loadTeams();
    }

    function viewTeam(teamUuid: string) {
        push(`/admin/teams/${teamUuid}`);
    }

    function handleRowKeydown(event: KeyboardEvent, teamUuid: string) {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            viewTeam(teamUuid);
        }
    }

    function formatDate(dateString?: string): string {
        if (!dateString) return '—';
        return new Date(dateString).toLocaleDateString(navigator.language, {
            dateStyle: 'medium',
        });
    }

    function ownerLabel(team: TeamRow): string {
        const owners = team.owners ?? [];
        if (owners.length === 0) return '—';
        const first = owners[0];
        const label = first.name?.trim() || first.email || 'Owner';
        if (owners.length === 1) return label;
        return `${label} +${owners.length - 1}`;
    }

    function teamStatus(team: TeamRow): { label: string; tone: StatusTone } {
        if (team.cancellation_date) {
            return { label: 'Cancelled', tone: 'danger' };
        }
        if (team.is_on_trial) {
            const days = team.trial_days_remaining;
            return {
                label: days != null ? `Trial (${days}d left)` : 'Trial',
                tone: 'warn',
            };
        }
        if (team.payment_plan?.name) {
            return { label: team.payment_plan.name, tone: 'success' };
        }
        return { label: 'No plan', tone: 'neutral' };
    }

    let searchDebounceTimer: ReturnType<typeof setTimeout>;
    function debounceSearch() {
        clearTimeout(searchDebounceTimer);
        searchDebounceTimer = setTimeout(() => loadTeams(), 300);
    }

    function onFilterChange() {
        loadTeams();
    }
</script>

<div class="page-shell admin-list">
    <div class="page-header">
        <div class="header-content">
            <h2 class="page-title">Teams</h2>
            <p class="page-subtitle">View all teams, plans, and membership</p>
        </div>
        <div class="header-stats">
            <span class="stat-badge">
                {filteredTeams.length} team{filteredTeams.length !== 1 ? 's' : ''}
            </span>
        </div>
    </div>

    <div class="filters-bar">
        <div class="filters-group">
            <div class="search-box">
                <input
                    type="text"
                    placeholder="Search by team name or owner..."
                    bind:value={searchQuery}
                    on:input={debounceSearch}
                    class="search-input"
                    aria-label="Search teams"
                />
            </div>
            <div class="filter-select">
                <label for="admin-team-status-filter" class="sr-only">Filter by status</label>
                <select id="admin-team-status-filter" bind:value={statusFilter} on:change={onFilterChange}>
                    {#each STATUS_OPTIONS as option}
                        <option value={option.value}>{option.label}</option>
                    {/each}
                </select>
            </div>
        </div>
        <span class="results-count">
            {filteredTeams.length} {filteredTeams.length === 1 ? 'team' : 'teams'}
        </span>
    </div>

    {#if isLoading}
        <div class="loading-state">Loading teams...</div>
    {:else if filteredTeams.length === 0}
        <div class="empty-state">
            <i class="users icon empty-icon" aria-hidden="true"></i>
            <p>No teams found.</p>
            {#if searchQuery || statusFilter}
                <button
                    type="button"
                    class="clear-filters-btn"
                    on:click={() => { searchQuery = ''; statusFilter = ''; loadTeams(); }}
                >
                    Clear filters
                </button>
            {/if}
        </div>
    {:else}
        <div class="table-wrap">
            <table class="data-table">
                <thead>
                    <tr>
                        <th class="sortable" on:click={() => handleSort('name')}>
                            <i class="users icon" aria-hidden="true"></i> Team
                            {#if sortField === 'name'}
                                <i class="icon {sortDirection === 'asc' ? 'arrow up' : 'arrow down'}" aria-hidden="true"></i>
                            {/if}
                        </th>
                        <th><i class="user icon" aria-hidden="true"></i> Owner</th>
                        <th><i class="credit card icon" aria-hidden="true"></i> Plan / status</th>
                        <th class="sortable center-col num-col" on:click={() => handleSort('active_member_count')}>
                            <i class="user friends icon" aria-hidden="true"></i> Members
                            {#if sortField === 'active_member_count'}
                                <i class="icon {sortDirection === 'asc' ? 'arrow up' : 'arrow down'}" aria-hidden="true"></i>
                            {/if}
                        </th>
                        <th class="center-col num-col"><i class="chair icon" aria-hidden="true"></i> Seats</th>
                        <th class="sortable date-col" on:click={() => handleSort('created_at')}>
                            <i class="calendar plus icon" aria-hidden="true"></i> Created
                            {#if sortField === 'created_at'}
                                <i class="icon {sortDirection === 'asc' ? 'arrow up' : 'arrow down'}" aria-hidden="true"></i>
                            {/if}
                        </th>
                        <th class="center-col actions-col">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {#each filteredTeams as team (team.uuid)}
                        {@const status = teamStatus(team)}
                        <tr
                            class="clickable-row data-row"
                            tabindex="0"
                            on:click={() => viewTeam(team.uuid)}
                            on:keydown={(e) => handleRowKeydown(e, team.uuid)}
                        >
                            <td class="name-cell">
                                <div class="team-name">{team.name}</div>
                                {#if team.billing_interval}
                                    <div class="team-meta">{team.billing_interval}</div>
                                {/if}
                            </td>
                            <td class="user-cell">
                                <div class="user-name">{ownerLabel(team)}</div>
                                {#if team.owners?.[0]?.email}
                                    <div class="user-email">{team.owners[0].email}</div>
                                {/if}
                            </td>
                            <td>
                                <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
                            </td>
                            <td class="center-col num-col">{team.active_member_count ?? 0}</td>
                            <td class="center-col num-col">{team.seat_count ?? '—'}</td>
                            <td class="date-cell date-col">{formatDate(team.created_at)}</td>
                            <td class="center-col actions-cell">
                                <button
                                    type="button"
                                    class="view-button"
                                    on:click|stopPropagation={() => viewTeam(team.uuid)}
                                >
                                    <i class="icon eye" aria-hidden="true"></i>
                                    View
                                </button>
                            </td>
                        </tr>
                    {/each}
                </tbody>
            </table>
        </div>
    {/if}
</div>

<style>
    .name-cell {
        min-width: 180px;
    }

    .team-name {
        font-weight: 500;
        color: var(--grey);
    }

    .team-meta {
        font-size: 0.8rem;
        color: var(--grey-secondary);
        text-transform: capitalize;
    }
</style>
